import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { utilStyles, uniqueId } from '@paypalcorp/pp-react-utils';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useMemo } from 'react';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { CriticalIcon } from '@paypalcorp/pp-react-icons';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorBackgroundSurfaceActive = tokens.sysColorBackgroundSurfaceActive,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysColorSelectedMain = tokens.sysColorSelectedMain,
      sysColorStructureBorderHighContrast = tokens.sysColorStructureBorderHighContrast,
      sysSizeCircleXs = tokens.sysSizeCircleXs,
      sysSizeTapTargetSm = tokens.sysSizeTapTargetSm,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceXl = tokens.sysSpaceXl,
      sysSpaceXs = tokens.sysSpaceXs;
  var radiobuttonItemTextSpacing = math("".concat(sysSpaceXl, " + ").concat(sysSpaceMd));

  var _textStyles = textStyles(tokens),
      textCaption = _textStyles.caption,
      fieldLabelLg = _textStyles.fieldLabelLg,
      fieldValueSm = _textStyles.fieldValueSm;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles; // Base Class for radio-group


  var radioGroupBase = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: radio_group_base;\n    display: flex; // to avoid collapsing margins\n    flex-direction: column;\n    margin: 0;\n    padding: 0;\n    border: none;\n    text-align: left;\n\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "]))); // Base Class for radio

  var radioBase = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: radio_base;\n    position: relative;\n    padding: ", " 0;\n    text-align: left;\n\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])), sysSpaceMd);
  var radioGroupLabel = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: radio_group_label;\n    ", ";\n    display: block;\n    padding-bottom: ", ";\n    margin-bottom: ", ";\n  "])), fieldLabelLg({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceMd, sysSpaceXs);
  var radioGroupLabelWithIcon = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: with_icon;\n    display: flex;\n    align-items: center;\n    min-height: ", ";\n    padding-bottom: 0;\n  "])), sysSizeTapTargetSm);
  var radioGroupLabelIcon = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: radio_group_label-icon;\n    margin-inline-start: ", ";\n  "])), sysSpaceSm);
  var radioLabel = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: radio_label;\n    -moz-osx-font-smoothing: grayscale;\n    cursor: pointer;\n    display: inline-block;\n    padding-left: ", ";\n    ", "\n    position: relative;\n    -webkit-tap-highlight-color: transparent;\n\n    [dir='rtl'] & {\n      padding-right: ", ";\n      padding-left: 0;\n    }\n  "])), radiobuttonItemTextSpacing, fieldValueSm(), radiobuttonItemTextSpacing);
  var checkIconContainer = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: check_icon_container;\n    border-radius: ", ";\n    border: ", " solid\n      ", ";\n    height: ", ";\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: ", ";\n    background-color: ", ";\n\n    [dir='rtl'] & {\n      right: 0;\n    }\n  "])), sysBorderRadiusInteractive, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, sysSizeCircleXs, sysSizeCircleXs, sysColorBackgroundSurface);
  var errorCheckIconContainer = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: error_check_icon_container;\n    border: ", " solid ", ";\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain);
  var checkIcon = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: check_icon;\n    display: none;\n  "])));
  var input = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: input;\n    cursor: pointer;\n    opacity: 0;\n    position: absolute;\n    margin: 0;\n\n    &:checked + label > span:first-of-type {\n      border-color: ", ";\n      background-color: ", ";\n      color: ", ";\n\n      span:first-of-type {\n        display: inline;\n      }\n    }\n\n    &:focus + label > span:first-of-type {\n      ", ";\n    }\n\n    &:active:not(:checked) + label > span:first-of-type {\n      background: ", ";\n    }\n  "])), sysColorSelectedMain, sysColorSelectedMain, sysColorBackgroundSurface, focusStyles.focusOffsetElement, sysColorBackgroundSurfaceActive);
  var inputWithError = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: input_with_error;\n    &:checked + label > span:first-of-type {\n      border: ", " solid ", ";\n    }\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain);
  var centeredLabelCheckIconContainer = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: centered_label_check_icon_container;\n    top: 50%;\n    transform: translateY(-50%);\n  "])));
  var helperText = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: helper_text;\n    ", ";\n    margin: 0;\n    list-style: none; // In case it's a list of helper texts\n    margin-top: ", ";\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceXs);
  var groupHelperText = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: group_helper_text;\n    ", ";\n    margin: 0;\n    list-style: none; // In case it's a list of helper texts\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var errorText = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: error_text;\n    ", ";\n    display: flex;\n    margin: 0;\n    align-items: center;\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var errorIcon = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: error_icon;\n    color: ", ";\n    margin-right: ", ";\n\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: 0;\n    }\n  "])), sysColorErrorMain, sysSpaceXs, sysSpaceXs);
  return {
    radioGroupBase: radioGroupBase,
    radioBase: radioBase,
    radioGroupLabel: radioGroupLabel,
    radioGroupLabelWithIcon: radioGroupLabelWithIcon,
    radioGroupLabelIcon: radioGroupLabelIcon,
    radioLabel: radioLabel,
    checkIconContainer: checkIconContainer,
    errorCheckIconContainer: errorCheckIconContainer,
    checkIcon: checkIcon,
    input: input,
    inputWithError: inputWithError,
    centeredLabelCheckIconContainer: centeredLabelCheckIconContainer,
    helperText: helperText,
    groupHelperText: groupHelperText,
    errorText: errorText,
    errorIcon: errorIcon
  };
}

var _excluded = ["label", "name", "value", "id", "centeredLabel", "helperText", "className", "labelClassName", "onChange", "checked", "errorText"];

var renderHelperText = function renderHelperText(text, helperTextCssVals) {
  return text && jsx("p", {
    css: helperTextCssVals,
    "data-ppui": "true"
  }, text);
};

var inferValue = function inferValue(label) {
  if (typeof label === 'string') {
    return label;
  }

  return 'value_not_set';
};

var RadioCheckIcon = function RadioCheckIcon(props) {
  return jsx("span", {
    css: props.checkIconCssVals,
    "aria-hidden": "true",
    "data-ppui": "true"
  }, jsx("svg", {
    viewBox: "0 0 100 100",
    xmlns: "http://www.w3.org/2000/svg",
    "data-ppui": "true"
  }, jsx("circle", {
    r: "18",
    cy: "50",
    cx: "50",
    strokeWidth: "0",
    fill: "currentColor",
    "data-ppui": "true"
  })));
};

var Radio = forwardRef(function (_ref, ref) {
  var label = _ref.label,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? uniqueId() : _ref$name,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? inferValue(label) : _ref$value,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? "Radio_".concat(name, "_").concat(value) : _ref$id,
      _ref$centeredLabel = _ref.centeredLabel,
      centeredLabel = _ref$centeredLabel === void 0 ? false : _ref$centeredLabel,
      helperText = _ref.helperText,
      className = _ref.className,
      labelClassName = _ref.labelClassName,
      onChange = _ref.onChange,
      _ref$checked = _ref.checked,
      checked = _ref$checked === void 0 ? false : _ref$checked,
      errorText = _ref.errorText,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var checkedPropName = onChange ? 'checked' : 'defaultChecked';
  var valueProps = {
    onChange: onChange
  };
  valueProps[checkedPropName] = checked;
  var inputCssVals = [_styles.input, !!errorText && _styles.inputWithError];
  var checkIconContainerCssVals = [_styles.checkIconContainer, !!errorText && _styles.errorCheckIconContainer, centeredLabel && _styles.centeredLabelCheckIconContainer];
  return jsx("div", {
    css: _styles.radioBase,
    className: className,
    "data-ppui-info": "radio_3.6.0"
  }, jsx("input", _extends({
    css: inputCssVals,
    type: "radio",
    name: name,
    value: value,
    id: id,
    ref: ref
  }, valueProps, rest, {
    "data-ppui": "true"
  })), jsx("label", {
    css: _styles.radioLabel,
    className: labelClassName,
    htmlFor: id,
    "data-ppui": "true"
  }, jsx("span", {
    css: checkIconContainerCssVals,
    "data-ppui": "true"
  }, jsx(RadioCheckIcon, {
    checkIconCssVals: _styles.checkIcon,
    "data-ppui": "true"
  })), label, renderHelperText(helperText, _styles.helperText)));
});
var _excluded$1 = ["as", "children", "className"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var FieldGroupLabel = function FieldGroupLabel(_ref) {
  var as = _ref.as,
      children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var componentProps = _objectSpread({
    className: className,
    children: children
  }, rest);

  return /*#__PURE__*/React.createElement(as || 'legend', _extends({}, componentProps, {
    "data-ppui": "true"
  }));
};

var FieldGroupMessage = function FieldGroupMessage(_ref) {
  var errorText = _ref.errorText,
      helperText = _ref.helperText,
      errorIconAriaLabel = _ref.errorIconAriaLabel,
      msgId = _ref.msgId,
      _styles = _ref._styles; // Get either the error message or helper text or... nothing

  var radioErrorIcon = jsx(CriticalIcon, {
    css: _styles.errorIcon,
    size: "sm",
    role: "img",
    "aria-label": errorIconAriaLabel,
    "data-ppui": "true"
  });
  return errorText && jsx("p", {
    css: _styles.errorText,
    id: msgId,
    "data-ppui": "true"
  }, radioErrorIcon, errorText) || helperText && jsx("p", {
    css: _styles.groupHelperText,
    id: msgId,
    "data-ppui": "true"
  }, helperText) || null;
};

var _excluded$2 = ["className", "radioLabelClassName", "errorText", "errorIconAriaLabel", "helperText", "id", "label", "name", "value", "icon", "options", "onChange"];

function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var RadioButton = function RadioButton(_ref) {
  var className = _ref.className,
      radioLabelClassName = _ref.radioLabelClassName,
      errorText = _ref.errorText,
      _ref$errorIconAriaLab = _ref.errorIconAriaLabel,
      errorIconAriaLabel = _ref$errorIconAriaLab === void 0 ? 'Error' : _ref$errorIconAriaLab,
      helperText = _ref.helperText,
      id = _ref.id,
      label = _ref.label,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'radioButton' : _ref$name,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      icon = _ref.icon,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var radioId = id !== null && id !== void 0 ? id : uniqueId('RadioButtton_');
  var msgId = "message_".concat(radioId);
  var fieldGroupLabelCssVal = [_styles.radioGroupLabel, icon && _styles.radioGroupLabelWithIcon];
  return (//@ts-ignore
    jsx("fieldset", _extends({
      css: _styles.radioGroupBase,
      className: className,
      "data-ppui-info": "radio_3.6.0"
    }, rest), jsx(FieldGroupLabel, {
      css: fieldGroupLabelCssVal,
      "data-ppui": "true"
    }, label, icon && jsx("span", {
      css: _styles.radioGroupLabelIcon,
      "data-ppui": "true"
    }, icon)), function renderRadios(optionsList, componentName, currentValue, changeHandler) {
      if (optionsList.length === 0) {
        return jsx("i", {
          "data-ppui": "true"
        }, "No Options!");
      }

      return optionsList.map(function (listProps, index) {
        var checkedPropName = changeHandler ? 'checked' : 'defaultChecked';

        var radioProps = _objectSpread$1(_objectSpread$1({}, listProps), {}, {
          labelClassName: radioLabelClassName
        });

        if (currentValue === listProps.value) {
          radioProps[checkedPropName] = true;
        }

        return jsx(Radio, _extends({
          key: "rbtn-item".concat(index),
          name: componentName,
          onChange: changeHandler,
          errorText: errorText
        }, radioProps, rest, {
          "data-ppui": "true"
        }));
      });
    }(options, name, value, onChange), jsx(FieldGroupMessage, {
      errorText: errorText,
      helperText: helperText,
      errorIconAriaLabel: errorIconAriaLabel,
      msgId: msgId,
      _styles: _styles,
      "data-ppui": "true"
    }))
  );
};

export { Radio, RadioButton, styles as radioStyles };